import { Injectable, inject } from '@angular/core';
import { BannerStore } from '@shared/stores/banner.store';
import type { BannerConfig } from '@shared/models/banner.model';

/**
 * Banner options type for convenience methods
 */
type BannerOptions = Partial<Omit<BannerConfig, 'id' | 'type' | 'title' | 'message'>>;

/**
 * BannerService
 * Public API for displaying app-wide banner notifications
 *
 * @description
 * Provides convenience methods for showing success, error, warning, and info banners.
 * Handles auto-dismiss timing and banner lifecycle.
 *
 * Features:
 * - Convenience methods for common banner types
 * - Smart defaults (success = 8s, error = manual)
 * - Returns banner ID for programmatic control
 * - Auto-dismiss with configurable duration
 *
 * @example
 * ```typescript
 * // Success banner (auto-dismiss after 8 seconds)
 * bannerService.success(
 *   'Vous recevrez le fichier à votre email',
 *   'Export effectué avec succès'
 * );
 *
 * // Error banner (manual dismiss only)
 * bannerService.error(
 *   'La connexion au serveur a échoué',
 *   'Erreur de connexion'
 * );
 *
 * // Custom banner with options
 * const bannerId = bannerService.show({
 *   type: 'warning',
 *   title: 'Attention',
 *   message: 'Votre session expire bientôt',
 *   icon: 'pi pi-exclamation-triangle',
 *   duration: 10000,
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class BannerService {
  private readonly store = inject(BannerStore);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Show a success banner
   *
   * @param message - Banner message
   * @param title - Banner title (default: "Succès")
   * @param options - Additional configuration options
   * @returns Banner ID
   */
  success(message: string, title = 'Succès', options?: BannerOptions): string {
    return this.show({
      type: 'success',
      title,
      message,
      icon: 'pi pi-check-circle',
      duration: 8000,
      dismissible: true,
      ...options,
    });
  }

  /**
   * Show an error banner
   *
   * @param message - Banner message
   * @param title - Banner title (default: "Erreur")
   * @param options - Additional configuration options
   * @returns Banner ID
   */
  error(message: string, title = 'Erreur', options?: BannerOptions): string {
    return this.show({
      type: 'error',
      title,
      message,
      icon: 'pi pi-times-circle',
      duration: null, // Error banners stay until manually dismissed
      dismissible: true,
      ...options,
    });
  }

  /**
   * Show a warning banner
   *
   * @param message - Banner message
   * @param title - Banner title (default: "Attention")
   * @param options - Additional configuration options
   * @returns Banner ID
   */
  warning(message: string, title = 'Attention', options?: BannerOptions): string {
    return this.show({
      type: 'warning',
      title,
      message,
      icon: 'pi pi-exclamation-triangle',
      duration: 10000,
      dismissible: true,
      ...options,
    });
  }

  /**
   * Show an info banner
   *
   * @param message - Banner message
   * @param title - Banner title (default: "Information")
   * @param options - Additional configuration options
   * @returns Banner ID
   */
  info(message: string, title = 'Information', options?: BannerOptions): string {
    return this.show({
      type: 'info',
      title,
      message,
      icon: 'pi pi-info-circle',
      duration: 8000,
      dismissible: true,
      ...options,
    });
  }

  /**
   * Show a custom banner
   * Generic method for full control over banner configuration
   *
   * @param config - Complete banner configuration
   * @returns Banner ID
   */
  show(config: Omit<BannerConfig, 'id'>): string {
    const bannerId = this.store.addBanner(config);

    // Set up auto-dismiss timer if duration is specified
    if (config.duration && config.duration > 0) {
      const timer = setTimeout(() => {
        this.dismiss(bannerId);
      }, config.duration);

      this.timers.set(bannerId, timer);
    }

    return bannerId;
  }

  /**
   * Dismiss a specific banner by ID
   *
   * @param id - Banner ID to dismiss
   */
  dismiss(id: string): void {
    // Clear any pending timer
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    // Remove banner from store
    this.store.removeBanner(id);
  }

  /**
   * Dismiss all active banners
   */
  dismissAll(): void {
    // Clear all timers
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();

    // Clear all banners from store
    this.store.clearAll();
  }
}
